import { Info } from "lucide-react";

import { getText, titleCase } from "shared/lib";

import { useGetObjectsNearAddress } from "shared/apiHooks";
import { useAccount, useIsReportReady } from "@/hooks";
import {lotSizeDescription} from "@/lib/utils.ts";

import {
  MapComponent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components";

export function LotPanel() {
  const { conversation } = useAccount();

  const isReportReady = useIsReportReady();

  const { data: nearObjectsResponse } = useGetObjectsNearAddress({
    path: {
      address_id: conversation?.address_data?.id || "",
    },
  });

  const getOverlays = () => {
    const overlays = conversation?.address_data?.overlays || [];

    if (overlays.length > 0) {
      return (
        <ul className="text-bodymd list-outside list-disc pl-5">
          {/* @ts-expect-error TODO: swagger type for overlays is incorrect */}
          {overlays.map((overlay, index) => {
            return (
              <li key={index + "lot-overlay"}>
                {titleCase(overlay.description || "")}
              </li>
            );
          })}
        </ul>
      );
    } else {
      return (
        <p className="text-bodymd">
          {getText("🌱 There are no overlays associated with this address.")}
        </p>
      );
    }
  };

  return (
    <TooltipProvider>
      <div className="flex w-full flex-col justify-start gap-5 align-top">
        <h1 className="text-headinglg font-medium">{getText("Lot")}</h1>

        <div className="flex flex-col gap-2">
          <label className="text-headingsm">{getText("Address")}</label>

          <p className="text-bodymd">
            {titleCase(conversation?.address_data?.unstructured_address || "")}
          </p>

          {isReportReady ? (
            <MapComponent
              latitude={conversation?.address_data?.gps_latitude ?? undefined}
              longitude={conversation?.address_data?.gps_longitude ?? undefined}
              addressBoundary={
                conversation?.address_data?.boundary ?? undefined
              }
              nearObjects={nearObjectsResponse?.data.near_objects ?? undefined}
              className="h-[200px] max-h-[200px] min-h-[200px] w-full rounded bg-[#E3E2E1]"
            />
          ) : null}
        </div>

        {conversation?.address_data?.lga_name ? (
          <div className="flex flex-col gap-2">
            <label className="text-headingsm">{getText("Council")}</label>

            <p className="text-bodymd capitalize">
              {conversation.address_data.lga_name.toLowerCase()}
            </p>
          </div>
        ) : null}

        {conversation?.address_data?.lot_size_sqm ? (
          <div className="flex flex-col gap-2">
            <label className="text-headingsm">{getText("Size")}</label>

            <p className="text-bodymd">{lotSizeDescription(conversation?.address_data)}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label className="text-headingsm">{getText("Zone")}</label>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent>
                {getText(
                  "Zones regulate the use and development of land. Different zones typically represent different types of land use, such as residential or commercial.",
                )}
              </TooltipContent>
            </Tooltip>
          </div>

          <p className="text-bodymd">
            {titleCase(conversation?.address_data?.zones[0]?.description || "")}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label className="text-headingsm">{getText("Overlays")}</label>

            <Tooltip>
              <TooltipTrigger>
                <Info className="h-3 w-3" />
              </TooltipTrigger>
              <TooltipContent className="flex flex-col gap-1">
                <p>
                  {getText(
                    "An overlay is a layer of additional planning controls that is applied to a specific area.",
                  )}
                </p>
                <p>
                  {getText(
                    "Overlays are used to address specific planning issues or to protect certain features or values in an area.",
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {getOverlays()}
        </div>
      </div>
    </TooltipProvider>
  );
}
